/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * TradingView Symbol Datafeed - provides candle data via pre-loaded cache or API fallback.
 * @see docs/TvADVANCED_API_FALLBACK.md for API fallback implementation details.
 */
import type * as T from "@src/types";
import type { ApiSymbol } from "@tradingdroid/frontends-types";

import { tdApi } from "@src/services/api/tdApi";

import * as h from "./helpers";

/**
 * Fetch live candles from API for API fallback mode.
 * Uses symbolInfo which already contains exchange and symbol name.
 * Throws on API errors - caller (getBars) handles error callback.
 */
const getLiveBars = async (
  symbolInfo: T.TV.LibrarySymbolInfo,
  resolution: T.TV.ResolutionString,
  periodParams: T.TV.PeriodParams,
): Promise<{ bars: T.TV.Bar[]; noData: boolean }> => {
  const { from, to, countBack } = periodParams;

  // Extract exchange and symbol from symbolInfo
  const exchange = symbolInfo.exchange;
  const symbol = symbolInfo.name;
  const period = h.tvResolutionToApiPeriod(resolution);

  // Convert Unix seconds to ISO strings for API
  const fromIso = new Date(from * 1000).toISOString();
  const toIso = new Date(to * 1000).toISOString();

  // The limit has to cover the range being asked for, not just the bars TradingView says it wants
  // back. countBack is what fits the viewport; the request can span far more than that, and a
  // limit sized to the viewport truncates the tail. The result is a chart that simply stops
  // partway with no error: 462 bars requested, 329 returned, three weeks missing.
  const periodMinutes = h.apiPeriodToMinutes(period);
  const spanBars = periodMinutes > 0 ? Math.ceil((to - from) / (periodMinutes * 60)) : 0;
  const needed = Math.max(countBack ?? 0, spanBars, 100);
  const limit = Math.min(needed, 5000);

  try {
    // Query by open_time range only - TradingView requests arbitrary time ranges
    // that don't align with candle boundaries, so close_time constraints fail.
    // Use open_time.lt (not le) to exclude candles starting exactly at 'to' time.
    // IMPORTANT: Sort ASC so we get candles from the START of the range first.
    // With DESC + limit, a large range (Nov 5 to Dec 23) would return only Dec data.
    const response = await tdApi.getCandles(exchange, symbol, period, {
      q: `open_time.ge:'${fromIso}' AND open_time.lt:'${toIso}'`,
      sort_by: "open_time:ASC",
      limit,
    });

    if (!response?.items?.length) {
      return { bars: [], noData: true };
    }

    return { bars: h.apiCandlesToBars(response.items), noData: false };
  } catch (error) {
    console.error("[getLiveBars] API fetch failed:", error);
    // Re-throw to let getBars handle the error callback (avoids double callback)
    throw error;
  }
};

/**
 * Creates a fresh datafeed instance for each TradingView widget
 * This prevents state pollution across widget recreations
 */
export const createSymbolDatafeed = (): T.ExternalDatafeed => {
  const datafeed: T.ExternalDatafeed = {
    state: {
      lastSearchIndex: 0,
      boundaryMarkers: null,
      onResetCacheNeeded: null,
      pendingResolutionChange: false, // Set true during resolution change to make getBars return noData
      isContinuousMarket: true, // Set in resolveSymbol based on symbol.kind
      earliestPositionTime: null, // Earliest position timestamp (seconds) for API fallback mode
      isCustomResolution: false, // Set true when resolution differs from original (enables API fetch on scroll)
    },
    props: {
      series: [],
      initial_capital: 0,
      metrics: {} as T.TDT.BacktestMetrics,
      marks: {
        opens: [],
        closes: [],
      },
      candles: {} as T.TypedArrayCandles,
      indicators: [],
      symbol: {} as ApiSymbol,
      positions: [],
    },

    onReady: (callback) => {
      // console.log('[datafeed.onReady]: Method call');
      setTimeout(() => {
        callback(h.configurationData);
      }, 10);
    },

    resolveSymbol: (fullSymbolName, onResolveCB, onErrorCB, extension) => {
      const tvSymbol = h.mapApiToTradingviewSymbol(datafeed.props.symbol);

      // Session configuration based on market type:
      // - "24x7": 24 hours, 7 days (crypto markets)
      // - "0000-0000": 24 hours Mon-Fri only (non-continuous markets like commodities, stocks)
      // This tells TradingView to expect gaps on weekends/holidays and not stop fetching
      const kind = String(datafeed.props.symbol.kind);
      const isContinuousMarket = kind === "crypto";

      datafeed.state.isContinuousMarket = isContinuousMarket; // Store for later use

      const session = isContinuousMarket ? "24x7" : "0000-0000";
      const hasEmptyBars = !isContinuousMarket;

      const tvFullSymbol: T.TV.LibrarySymbolInfo = {
        ...tvSymbol,
        name: tvSymbol.symbol, // Symbol name only (per TradingView docs)
        ticker: tvSymbol.ticker, // Use ticker for EXCHANGE:SYMBOL format display
        session,
        timezone: "Etc/UTC",
        listed_exchange: datafeed.props.symbol.exchange,
        exchange: datafeed.props.symbol.exchange,
        format: "price",
        // TODO: find the proper formula for this, hardcoded values for stablecoin pairs
        pricescale: 10000,
        minmov: 1,
        has_intraday: true,
        has_empty_bars: hasEmptyBars,
        has_weekly_and_monthly: false,
        volume_precision: 2,
        data_status: "endofday",
      };

      setTimeout(() => {
        onResolveCB(tvFullSymbol);
      }, 10);
    },

    searchSymbols: (userInput, exchange, symbolType, onResultCB) => {
      // TODO: implement searchSymbols if live symbol search is required
    },

    // NOTE: getBars is intentionally non-async to match TradingView's callback-based API signature.
    // The API fallback path uses .then()/.catch() for promise resolution.
    getBars: (symbolInfo, resolution, periodParams, onResultCB, onErrorCB) => {
      const { from, to, firstDataRequest } = periodParams;
      const { times } = datafeed.props.candles;

      // Resolution change in progress - return noData to prevent caching old data
      if (datafeed.state.pendingResolutionChange) {
        onResultCB([], { noData: true });
        return;
      }

      // API Fallback: forward to getLiveBars when no pre-loaded candles
      if (times.length === 0 && datafeed.state.useApiFallback) {
        getLiveBars(symbolInfo, resolution, periodParams)
          .then(({ bars, noData }) => {
            // For API fallback with positions, we need careful noData handling:
            // - Only return noData=true if we're BEFORE the earliest position time
            // - Otherwise return noData=false so TradingView keeps fetching through gaps
            // This handles the case where positions are from weeks/months ago with data gaps
            const earliestPos = datafeed.state.earliestPositionTime;
            let shouldReturnNoData = noData;

            if (earliestPos && noData) {
              // Only return noData=true if entire request range is before earliest position
              shouldReturnNoData = periodParams.to < earliestPos;
            } else if (!earliestPos && noData) {
              // No positions, use standard behavior
              shouldReturnNoData = datafeed.state.isContinuousMarket
                ? noData
                : noData && periodParams.firstDataRequest;
            }

            // Store the latest price for unrealized PnL calculation
            if (bars.length > 0) {
              // Bars are sorted ASC (oldest first after sorting in apiCandlesToBars)
              datafeed.state.latestPrice = bars[bars.length - 1].close;
            }
            onResultCB(bars, { noData: shouldReturnNoData });
          })
          .catch((error) => onErrorCB(String(error)));
        return;
      }

      // No candles and no fallback
      if (times.length === 0) {
        onResultCB([], { noData: true });
        return;
      }

      // Pre-loaded candles path (synchronous)
      try {
        // Check if request is outside our data range
        const oldestTime = times.at(-1)!;
        const newestTime = times.at(0)!;
        // Request is MORE RECENT than our data (e.g., requesting Feb 2026 when data ends Nov 2025)
        const isAfterOurData = from > newestTime;
        // Request is OLDER than our data (e.g., requesting Sep 2025 when data starts Oct 2025)
        const isBeforeOurData = to < oldestTime;

        if (isAfterOurData && firstDataRequest) {
          // FIRST request is after our data - return ALL our data so TradingView has something
          // to display, then setVisibleRange will scroll to the correct position
          const allBars = new Array(times.length);
          for (let i = times.length - 1; i >= 0; i--) {
            allBars[times.length - 1 - i] = {
              time: times[i] * 1000,
              open: datafeed.props.candles.opens[i],
              high: datafeed.props.candles.highs[i],
              low: datafeed.props.candles.lows[i],
              close: datafeed.props.candles.closes[i],
              volume: datafeed.props.candles.volumes[i],
            };
          }
          onResultCB(allBars, { noData: false });
          return;
        }

        if (isAfterOurData || isBeforeOurData) {
          // Custom resolution: fetch incrementally from API instead of stopping
          if (datafeed.state.isCustomResolution) {
            const origRange = datafeed.state.originalTimeRange;
            // Only fetch if request overlaps with the original backtest time range
            const withinOriginal = !origRange || (from < origRange.to && to > origRange.from);

            if (withinOriginal) {
              getLiveBars(symbolInfo, resolution, periodParams)
                .then(({ bars, noData }) => {
                  // For non-continuous markets (commodities), API may return 0 bars for
                  // trading gaps (nights/weekends). Don't return noData=true or TradingView
                  // stops fetching entirely. Return noData=false so it keeps requesting
                  // further back through the gap until it finds data or hits range boundary.
                  let shouldReturnNoData = noData;
                  if (noData && origRange) {
                    shouldReturnNoData = from < origRange.from;
                  }
                  onResultCB(bars, { noData: shouldReturnNoData });
                })
                .catch((error) => onErrorCB(String(error)));
              return;
            }
          }

          // Request is outside our data range - return noData=true to stop fetching
          onResultCB([], { noData: true });
          return;
        }

        let fromIndex: number;
        const lastIndex = datafeed?.state?.lastSearchIndex;

        if (from < times[datafeed?.state?.lastSearchIndex]) {
          fromIndex = h.binarySearch(times, from, lastIndex, times.length - 1);
        } else {
          fromIndex = h.binarySearch(times, from, 0, times.length - 1);
        }
        datafeed.state.lastSearchIndex = fromIndex;

        let toIndex = h.binarySearch(times, to);

        // Clamp indices to valid array bounds
        const maxIndex = times.length - 1;
        fromIndex = Math.min(Math.max(fromIndex, 0), maxIndex);
        toIndex = Math.min(Math.max(toIndex, 0), maxIndex);

        // Ensure fromIndex >= toIndex (since we iterate backwards in descending order)
        if (fromIndex < toIndex) {
          [fromIndex, toIndex] = [toIndex, fromIndex];
        }

        const totalBars = fromIndex - toIndex + 1;
        const bars = new Array(totalBars);
        let idx = 0;

        for (let i = fromIndex; i >= toIndex; i--) {
          const timeS = times[i];
          if (!timeS) break;

          const bar = {
            time: timeS * 1000,
            open: datafeed.props.candles.opens[i],
            high: datafeed.props.candles.highs[i],
            low: datafeed.props.candles.lows[i],
            close: datafeed.props.candles.closes[i],
            volume: datafeed.props.candles.volumes[i],
          };

          bars[idx++] = bar;
        }

        // Trim array to actual filled size (gaps are handled by TradingView via has_empty_bars)
        const actualBars = bars.slice(0, idx);
        onResultCB(actualBars, { noData: false });
      } catch (error) {
        onErrorCB(error as string);
      }
    },

    subscribeBars: (symbolInfo, resolution, onTickCB, listenerGuid, onResetCacheNeededCB) => {
      // Store the reset callback for resolution change handling
      datafeed.state.onResetCacheNeeded = onResetCacheNeededCB;
    },

    unsubscribeBars: (listenerGuid) => {
      // console.log('[unsubscribeBars]: Method call with listenerGuid:', listenerGuid);
    },

    getMarks: (symbolInfo, from, to, onDataCB, resolution) => {
      // console.log("[getMarks]]", from, to);
      const marksInRange = datafeed.props.marks.opens.filter((m) => m.time >= from && m.time <= to);
      onDataCB(marksInRange);
    },

    getTimescaleMarks: (symbolInfo, from, to, onDataCB, resolution) => {
      // console.log("[getTimescaleMarks]]", from, to);
      const marksInRange = datafeed.props.marks.closes.filter(
        (m) => m.time >= from && m.time <= to,
      );
      onDataCB(marksInRange);
    },

    getQuotes: (symbols, onDataCB, onErrorCB) => {
      // console.log('[getQuotes]: Method call with symbols:', symbols);
    },

    subscribeQuotes: (symbols, fastSymbols, onRealtimeCB, listenGuid) => {
      // console.log('[subscribeQuotes]: Method call with symbols - listenerGuid:', symbols, listenGuid);
    },

    unsubscribeQuotes: (listenerGuid) => {
      // console.log('[unsubscribeQuotes]: Method call with listenerGuid:', listenerGuid);
    },

    /**
     * Update candle data when resolution changes
     * This replaces the current candle data with new data from API
     */
    updateCandleData: (newCandles: T.TypedArrayCandles) => {
      datafeed.props.candles = newCandles;
      datafeed.state.lastSearchIndex = 0;
      datafeed.state.boundaryMarkers = null;
      datafeed.state.pendingResolutionChange = false; // Clear flag - data is ready
    },

    /**
     * Mark that a resolution change is starting - getBars will return noData until updateCandleData is called
     */
    setPendingResolutionChange: (pending: boolean) => {
      datafeed.state.pendingResolutionChange = pending;
    },
  };

  return datafeed;
};

// Deprecated: Keep for backward compatibility, but prefer createSymbolDatafeed()
const symbolDatafeed = createSymbolDatafeed();
export default symbolDatafeed;
